export const SUPPORTED_CODES = ['en', 'fi', 'ru', 'uk', 'he', 'de', 'pl', 'et'] as const

export type LocaleCode = (typeof SUPPORTED_CODES)[number]

export const DEFAULT_LOCALE: LocaleCode = 'en'

export function isSupportedCode(value: string | null | undefined): value is LocaleCode {
  return !!value && (SUPPORTED_CODES as readonly string[]).includes(value)
}

/**
 * Decides the starting language, most explicit signal first.
 *
 * Lives in its own module because it is needed twice, at two different moments: once when
 * i18next initialises (before React renders anything) and once when the provider sets up its
 * state. Resolving it in only one of those places is what causes a page to render its chrome
 * in one language and its labels in another.
 *
 * `?lang=` wins because that is what a shared or indexed URL carries, then the visitor's own
 * previous choice, then the browser. Anything unrecognised falls to English here; the API is
 * the component that complains about an unsupported code.
 */
export function resolveInitialLocale(): LocaleCode {
  if (typeof window === 'undefined') {
    return DEFAULT_LOCALE
  }
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('lang')
    if (isSupportedCode(fromQuery)) return fromQuery

    const stored = window.localStorage.getItem('lakeside.locale')
    if (isSupportedCode(stored)) return stored

    for (const candidate of navigator.languages ?? []) {
      const base = candidate.toLowerCase().split('-')[0]
      // Java and older browsers still emit the legacy tags for Hebrew and Ukrainian.
      const normalised = base === 'iw' ? 'he' : base === 'ua' ? 'uk' : base
      if (isSupportedCode(normalised)) return normalised
    }
  } catch {
    // Private browsing can throw on localStorage. English is a fine answer.
  }
  return DEFAULT_LOCALE
}
